import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, TrendingUp, TrendingDown, Building2, DollarSign, BarChart3, PieChart, Target, Calendar, BarChart, Star, Award, Zap, Shield, Activity, Globe } from 'lucide-react';

const CompanyProfileModal = ({ symbol, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    setError('');
    setProfile(null);
    // Always strip .L for backend API
    const baseSymbol = symbol.endsWith('.L') ? symbol.replace('.L', '') : symbol;
    const fetchProfile = () => {
      setLoading(true);
      setError('');
      setProfile(null);
      axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/market-data/company-profile/?symbol=${baseSymbol}`)
        .then(res => setProfile(res.data))
        .catch(() => setError('Failed to fetch company profile.'))
        .finally(() => setLoading(false));
    };
    fetchProfile();
    const interval = setInterval(fetchProfile, 25000);
    return () => clearInterval(interval);
  }, [symbol]);

  if (!symbol) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden relative border border-gray-200 dark:border-gray-700">
        {/* Professional Header */}
        <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-4">
          {/* Subtle Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-500/10"></div>
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-10 translate-x-10"></div>
          
          {/* Close Button */}
          <button 
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 hover:scale-105 z-20"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            type="button"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
          
          {/* Header Content */}
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm border border-white/30">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full border border-white animate-pulse"></div>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white mb-1">
                  {profile?.name || symbol || 'Loading...'}
                </h1>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-white/20 rounded-full text-white/90 text-xs font-medium backdrop-blur-sm">
                    {profile?.symbol || symbol}
                  </span>
                  <div className="flex items-center space-x-0.5">
                    <Star className="h-3 w-3 text-yellow-400 fill-current" />
                    <Star className="h-3 w-3 text-yellow-400 fill-current" />
                    <Star className="h-3 w-3 text-yellow-400 fill-current" />
                    <Star className="h-3 w-3 text-yellow-400 fill-current" />
                    <Star className="h-3 w-3 text-yellow-400 fill-current" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Live Status Indicator */}
            <div className="flex items-center space-x-1.5 px-2 py-1 bg-green-500/20 rounded-full border border-green-400/30 backdrop-blur-sm">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-300 text-xs font-medium">LIVE</span>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-4 max-h-[calc(80vh-100px)] overflow-y-auto bg-white dark:bg-gray-900">
          {loading && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
              <span className="mt-3 text-sm text-gray-600 dark:text-gray-300 font-medium">Loading company data...</span>
            </div>
          )}
          
          {error && (
            <div className="text-center py-8">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="text-red-600 dark:text-red-400 text-lg font-semibold mb-2">⚠️ Unable to Load Data</div>
                <div className="text-red-500 dark:text-red-300 text-sm">{error}</div>
              </div>
            </div>
          )}
          
        {profile && (
            <div className="space-y-4">
              {/* Price Overview Section */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Main Price Display */}
                  <div className="lg:col-span-2">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Current Price</h3>
                        <p className="text-gray-500 dark:text-gray-400 text-xs">Real-time market data</p>
                      </div>
              </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  £{profile.price ? profile.price.toFixed(2) : 'N/A'}
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg ${profile.today_change > 0 ? 'bg-green-100 dark:bg-green-900/30' : profile.today_change < 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                        {profile.today_change > 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : profile.today_change < 0 ? (
                          <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />
                        ) : (
                          <Activity className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        )}
                        <span className={`font-semibold text-sm ${profile.today_change > 0 ? 'text-green-600 dark:text-green-400' : profile.today_change < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          {profile.today_change !== undefined && profile.today_change !== null ? 
                            (profile.today_change > 0 ? '+' : '') + profile.today_change.toFixed(2) : 'N/A'}
                        </span>
                        {profile.today_change_percent !== undefined && profile.today_change_percent !== null && (
                          <span className={`text-xs ${profile.today_change_percent > 0 ? 'text-green-600 dark:text-green-400' : profile.today_change_percent < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                            ({profile.today_change_percent > 0 ? '+' : ''}{profile.today_change_percent.toFixed(2)}%)
                </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Volume Display */}
                  <div className="flex flex-col justify-center">
                    <div className="text-center">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg mb-2">
                        <BarChart className="h-5 w-5 text-orange-600 dark:text-orange-400 mx-auto" />
                      </div>
                      <div className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                        {profile.volume ? (profile.volume / 1000000).toFixed(1) + 'M' : 'N/A'}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs">Trading Volume</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* P/E Ratio */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <BarChart3 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <Award className="h-3 w-3 text-blue-500 dark:text-blue-400" />
                  </div>
                  <div className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                    {profile.pe_ratio ? profile.pe_ratio.toFixed(2) : 'N/A'}
                  </div>
                  <div className="text-blue-600 dark:text-blue-400 text-xs font-medium">P/E Ratio</div>
                </div>

                {/* P/B Ratio */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <PieChart className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <Shield className="h-3 w-3 text-purple-500 dark:text-purple-400" />
                  </div>
                  <div className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                    {profile.pb_ratio ? profile.pb_ratio.toFixed(2) : 'N/A'}
                  </div>
                  <div className="text-purple-600 dark:text-purple-400 text-xs font-medium">P/B Ratio</div>
                </div>

                {/* Dividend Yield */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="p-1.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                      <Target className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <Zap className="h-3 w-3 text-yellow-500 dark:text-yellow-400" />
                  </div>
                  <div className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                    {profile.dividend_yield ? `${profile.dividend_yield.toFixed(2)}%` : 'N/A'}
                  </div>
                  <div className="text-yellow-600 dark:text-yellow-400 text-xs font-medium">Dividend Yield</div>
                </div>

                {/* 52-Week Range */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                      <Calendar className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <Globe className="h-3 w-3 text-emerald-500 dark:text-emerald-400" />
                  </div>
                  <div className="space-y-1.5">
                    <div>
                      <div className="text-base font-bold text-green-600 dark:text-green-400">
                        {profile['52_week_high'] ? `£${profile['52_week_high'].toFixed(2)}` : 'N/A'}
                      </div>
                      <div className="text-emerald-600 dark:text-emerald-400 text-xs font-medium">52W High</div>
                    </div>
                    <div>
                      <div className="text-base font-bold text-red-600 dark:text-red-400">
                        {profile['52_week_low'] ? `£${profile['52_week_low'].toFixed(2)}` : 'N/A'}
              </div>
                      <div className="text-emerald-600 dark:text-emerald-400 text-xs font-medium">52W Low</div>
              </div>
              </div>
              </div>
              </div>

              {/* Company Description */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <Building2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Company Overview</h3>
                      <p className="text-gray-500 dark:text-gray-400 text-xs">Detailed business information</p>
              </div>
            </div>
                  {profile.description ? (
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
                  {profile.description}
                </p>
              </div>
                  ) : (
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600 text-center">
                      <p className="text-gray-500 dark:text-gray-400 italic text-sm">
                  Company description not available
                </p>
              </div>
            )}
              </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileModal; 